import { useRef, useState } from 'react';
import { api } from '../../api/client.js';
import { useToast } from '../../state/ToastContext.jsx';
import { Confirm } from '../../components/ui.jsx';

/**
 * Pictures and videos for one listing. Order matters: the first image is the one
 * tenants see in search results, so it can be moved with the arrow buttons.
 */
const MediaManager = ({ propertyId, items, onChange }) => {
  const toast = useToast();
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [videoLink, setVideoLink] = useState('');
  const [addingLink, setAddingLink] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (const file of [...files].slice(0, 12)) formData.append('files', file);

      const result = await api.upload(`/properties/${propertyId}/media`, formData);
      onChange([...items, ...result.items]);

      if (result.rejected?.length) {
        for (const rejection of result.rejected) toast.error(`${rejection.name}: ${rejection.reason}`);
      }
      if (result.items.length > 0) {
        toast.success(`${result.items.length} file${result.items.length === 1 ? '' : 's'} added.`);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const addLink = async (event) => {
    event.preventDefault();
    setAddingLink(true);
    try {
      const { item } = await api.post(`/properties/${propertyId}/media/link`, { url: videoLink.trim() });
      onChange([...items, item]);
      setVideoLink('');
      toast.success('Video link added.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAddingLink(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/media/${removing.id}`);
      onChange(items.filter((item) => item.id !== removing.id));
      setRemoving(null);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;

    const reordered = [...items];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    onChange(reordered);

    try {
      await api.put(`/properties/${propertyId}/media/order`, { order: reordered.map((item) => item.id) });
    } catch (error) {
      onChange(items);
      toast.error(error.message);
    }
  };

  const images = items.filter((item) => item.type === 'image');

  return (
    <div className="media-manager">
      <div className="media-actions">
        <label className={`btn btn-primary ${uploading ? 'is-busy' : ''}`}>
          {uploading ? 'Uploading...' : 'Add pictures or a video'}
          <input
            ref={fileInput}
            type="file"
            hidden
            multiple
            accept="image/*,video/*"
            disabled={uploading}
            onChange={(event) => upload(event.target.files)}
          />
        </label>

        <form className="link-form" onSubmit={addLink}>
          <input
            type="url"
            value={videoLink}
            onChange={(event) => setVideoLink(event.target.value)}
            placeholder="Or paste a YouTube link"
          />
          <button type="submit" className="btn btn-outline" disabled={addingLink || !videoLink.trim()}>
            {addingLink ? 'Adding...' : 'Add link'}
          </button>
        </form>
      </div>

      <p className="field-hint">
        Pictures up to 8 MB each, videos up to 80 MB. Take them in daylight and include the toilet, the kitchen and the
        compound: those are the three things every tenant asks about.
      </p>

      {items.length === 0 ? (
        <p className="media-empty">No pictures yet. A listing without photos gets very few applications.</p>
      ) : (
        <ul className="media-grid">
          {items.map((item, index) => (
            <li key={item.id} className={`media-tile ${index === 0 && item.type === 'image' ? 'is-cover' : ''}`}>
              <div className="media-thumb">
                {item.type === 'image' ? (
                  <img src={item.url} alt={item.caption || ''} />
                ) : item.thumbnailUrl ? (
                  <img src={item.thumbnailUrl} alt="" />
                ) : (
                  <span className="media-video-mark">Video</span>
                )}
                {item.type === 'video' ? <span className="media-badge">Video</span> : null}
                {index === 0 && item.type === 'image' ? <span className="media-badge cover-badge">Cover</span> : null}
              </div>

              <div className="media-tile-actions">
                <button type="button" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Move earlier">
                  &#8592;
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === items.length - 1}
                  aria-label="Move later"
                >
                  &#8594;
                </button>
                <button type="button" className="danger-link" onClick={() => setRemoving(item)} aria-label="Remove">
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {items.length > 0 && images.length === 0 ? (
        <p className="notice notice-warn">
          This listing only has video. Add at least one picture, because search results need a cover image.
        </p>
      ) : null}

      {removing ? (
        <Confirm
          title="Remove this file?"
          message="It will be deleted from the listing straight away."
          confirmLabel="Remove"
          busy={busy}
          onConfirm={remove}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
};

export default MediaManager;
